import { useDraggable, useDroppable } from '@dnd-kit/core';
import { observer } from 'mobx-react';
import { useCallback } from 'react';
import deliveryZonesStore from '../../stores/deliveryZonesStore';
import { Icons } from 'src/assets/fonts/Icons/icons';
import { FORM_MODE } from 'src/constants/main';
import theme from 'src/constants/styles';
import { localeStore } from 'src/mobx/localesStore';
import { IDeliveryZone } from 'src/types/deliveryManagment';
import { getTranslate } from 'src/utils/helpers';
import { getDurations } from 'src/utils/styles';
import { ColorBox } from 'src/view/components/Controls/ColorBox/ColorBox';
import Icon from 'src/view/components/Controls/Icon/Icon';
import './style.scss';

interface IProps {
  id: number;
  zone: IDeliveryZone;
  index: number;
  overlay?: boolean;
  className?: string;
}

export const DeliveryZoneComponent = observer(({ zone, index, overlay, ...props }: IProps) => {
  const { isOver, setNodeRef: droppableRef } = useDroppable({ id: props.id.toString() });
  const { attributes, isDragging, listeners, setNodeRef: draggableRef } = useDraggable({ id: props.id.toString() });

  const selectZone = useCallback(() => deliveryZonesStore.selectZone(zone), [zone]);

  const editZone = useCallback(() => deliveryZonesStore.selectDraft(zone, FORM_MODE.EDIT), [zone]);

  const getClassName = useCallback(() => {
    let className: string = 'delivery-zone-item';
    if (!overlay && !isDragging && !deliveryZonesStore.zone) {
      className += ' animate__animated animate__fast animate__fadeInRight';
    }
    if (isDragging) {
      className += ' delivery-zone-item__drag-clone';
    } else if (isOver) {
      className += ' delivery-zone-item__droppable';
    }
    if (zone.id === deliveryZonesStore.zone?.id) className += ' active';
    if (props.className) className += ` ${props.className}`;
    return className;
  }, [overlay, isDragging, isOver, zone.id, props.className]);

  return (
    <div
      id={`zone_${zone.id}`}
      ref={!deliveryZonesStore.draggableZone ? draggableRef : droppableRef}
      style={{ animationDelay: `${getDurations({ max: 6, index, diff: 75 })}ms` }}
      onClick={selectZone}
      className={getClassName()}
    >
      <div className="d-flex mb-4 align-items-center justify-content-between">
        <div className="d-flex align-items-center flex-1">
          {/* eslint-disable-next-line react/jsx-props-no-spreading */}
          <div className="delivery-zone-item__icon-drag-wrapper mr-1" {...listeners} {...attributes}>
            <Icon name={Icons.DragHand} color={theme.colors.gray2} size={14} className="delivery-zone-item__icon-drag" />
          </div>
          <span className="ml-1 delivery-zone-item__priority">{zone.priority}</span>
          <span className="ml-2 delivery-zone-item__name">
            <p className="text-size-20 family-bold text-elipsis">{getTranslate(zone.name)}</p>
          </span>
        </div>
        <span className="delivery-zone-item__color ml-4">
          <ColorBox color={deliveryZonesStore.getColor(zone)} />
        </span>
        <span className="ml-4" onClick={editZone}>
          <Icon name={Icons.Pencil} color={theme.colors.mainblue} />
        </span>
      </div>
      <div className="d-flex text-size-18 align-items-center">
        <span className="family-medium mr-4">{localeStore.t('Branch')}</span>
        {zone.branch ? (
          <span className={`family-semibold text-elipsis ${zone.branch.is_delete ? 'text-through' : ''} `}>{getTranslate(zone.branch.name)}</span>
        ) : null}
        {/* {zone.city ? <span className={'family-semibold text-elipsis'}>
        {getTranslate(zone.city.name)}</span> : null} */}
      </div>
    </div>
  );
});
